import React, { useState, useEffect } from "react";
import TeacherLayout from "../../layout/teacherLayout";
import { collection, query, where, getDocs, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";

export default function Students() {
    const teacher_id = localStorage.getItem("uuid");

    const [classes, setClasses] = useState([]);
    const [selectedClassCode, setSelectedClassCode] = useState("ALL");
    const [selectedStatus, setSelectedStatus] = useState("ALL");
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);

    const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("gst");
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [studentGstData, setStudentGstData] = useState(null);
    const [studentGstHistory, setStudentGstHistory] = useState([]);
    const [activeGstAttemptIndex, setActiveGstAttemptIndex] = useState(0);
    const [showRetakeModal, setShowRetakeModal] = useState(false);
    const [showIndRetakeModal, setShowIndRetakeModal] = useState(false);
    const [studentIndData, setStudentIndData] = useState(null);
    const [studentIndHistory, setStudentIndHistory] = useState([]);
    const [activeIndAttemptIndex, setActiveIndAttemptIndex] = useState(0);
    const [studentModuleData, setStudentModuleData] = useState(null);
    const [teacherNotes, setTeacherNotes] = useState("");
    const [isSavingNotes, setIsSavingNotes] = useState(false);
    const [loadingAssessment, setLoadingAssessment] = useState(false);
    const [selectedStudentName, setSelectedStudentName] = useState("");
    const [feedbackModal, setFeedbackModal] = useState({ show: false, message: "", isError: false });

    const fetchData = async () => {
        setLoading(true);
        try {
            // 1. Fetch teacher's classes
            const classesQuery = query(
                collection(db, "classes"),
                where("teacher_id", "==", teacher_id)
            );
            const classesSnapshot = await getDocs(classesQuery);
            const teacherClasses = classesSnapshot.docs.map(doc => doc.data());
            setClasses(teacherClasses);

            const teacherClassCodes = teacherClasses.map(c => c.class_code);

            // 2. Fetch all students
            const studentsQuery = query(
                collection(db, "users"),
                where("role", "==", "student")
            );
            const studentsSnapshot = await getDocs(studentsQuery);
            let allStudents = studentsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Sort by latest registered first
            allStudents.sort((a, b) => {
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                return dateB - dateA;
            });

            // 3. Filter students who belong to any of this teacher's classes
            const myStudents = allStudents.filter(student =>
                teacherClassCodes.includes(student.classCode)
            );

            setStudents(myStudents);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (teacher_id) {
            fetchData();
        }
    }, [teacher_id]);

    const getStudentStatus = (student) => {
        if (!student.gst_assessment_attempted) return "PENDING_GST";
        if (student.gst_score >= 14 || student.gst_status === "PASSED_GST") return "PASSED_GST";
        if (student.individualized_assessment_attempted) return "COMPLETED_IND";
        return "NEEDS_IND";
    };

    const [searchTerm, setSearchTerm] = useState("");

    // Filter students based on dropdown selections and search term
    let displayedStudents = selectedClassCode === "ALL"
        ? students
        : students.filter(s => s.classCode === selectedClassCode);

    if (selectedStatus !== "ALL") {
        displayedStudents = displayedStudents.filter(s => getStudentStatus(s) === selectedStatus);
    }

    if (searchTerm.trim() !== "") {
        const queryStr = searchTerm.toLowerCase();
        displayedStudents = displayedStudents.filter(s => {
            const name = `${s.first_name || ""} ${s.last_name || ""}`.toLowerCase();
            const email = (s.email || "").toLowerCase();
            const classCode = (s.classCode || "").toLowerCase();
            return name.includes(queryStr) || email.includes(queryStr) || classCode.includes(queryStr);
        });
    }

    // Pagination state (default 10 rows)
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [studentSortConfig, setStudentSortConfig] = useState({ key: "name", direction: "asc" });

    const handleStudentSort = (key) => {
        let direction = "asc";
        if (studentSortConfig.key === key && studentSortConfig.direction === "asc") {
            direction = "desc";
        }
        setStudentSortConfig({ key, direction });
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedClassCode, selectedStatus, searchTerm]);

    // Sort students
    const sortedDisplayedStudents = [...displayedStudents].sort((a, b) => {
        let valA, valB;
        if (studentSortConfig.key === "name") {
            valA = `${a.first_name || ""} ${a.last_name || ""}`.toLowerCase();
            valB = `${b.first_name || ""} ${b.last_name || ""}`.toLowerCase();
        } else if (studentSortConfig.key === "date_registered") {
            valA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
            valB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
        } else if (studentSortConfig.key === "grade") {
            valA = (a.grade_level || "").toString().toLowerCase();
            valB = (b.grade_level || "").toString().toLowerCase();
        } else if (studentSortConfig.key === "class_code") {
            valA = (a.classCode || "").toString().toLowerCase();
            valB = (b.classCode || "").toString().toLowerCase();
        } else if (studentSortConfig.key === "gst_date") {
            valA = a.assessment_date ? new Date(a.assessment_date).getTime() : 0;
            valB = b.assessment_date ? new Date(b.assessment_date).getTime() : 0;
        } else if (studentSortConfig.key === "gst_score") {
            valA = a.gst_score || 0;
            valB = b.gst_score || 0;
        } else if (studentSortConfig.key === "status") {
            valA = getStudentStatus(a).toLowerCase();
            valB = getStudentStatus(b).toLowerCase();
        }

        if (valA < valB) return studentSortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return studentSortConfig.direction === "asc" ? 1 : -1;
        return 0;
    });

    const totalStudents = sortedDisplayedStudents.length;
    const totalPages = Math.ceil(totalStudents / rowsPerPage) || 1;
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedStudents = sortedDisplayedStudents.slice(startIndex, startIndex + rowsPerPage);

    const getStatusBadge = (status) => {
        switch (status) {
            case "PENDING_GST":
                return <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs font-bold">Pending</span>;
            case "PASSED_GST":
                return <span className="px-2 py-1 bg-green-200 text-green-800 rounded text-xs font-bold">Passed (GST)</span>;
            case "INDEPENDENT":
                return <span className="px-2 py-1 bg-green-200 text-green-800 rounded text-xs font-bold">Independent</span>;
            case "COMPLETED_IND":
                return <span className="px-2 py-1 bg-blue-200 text-blue-800 rounded text-xs font-bold">Completed</span>;
            case "NEEDS_IND":
                return <span className="px-2 py-1 bg-yellow-200 text-yellow-800 rounded text-xs font-bold">To Assess</span>;
            default:
                return null;
        }
    };

    const handleViewAssessment = async (student) => {
        setSelectedStudent(student);
        setSelectedStudentName(`${student.first_name} ${student.last_name}`);
        setAssessmentModalOpen(true);
        setActiveTab("overview");
        setLoadingAssessment(true);
        setStudentGstData(null);
        setStudentGstHistory([]);
        setActiveGstAttemptIndex(0);
        setStudentIndData(null);
        setStudentIndHistory([]);
        setActiveIndAttemptIndex(0);
        setStudentModuleData(null);
        setTeacherNotes("");

        try {
            // Fetch historical GST attempts
            const gstAttemptsRef = collection(db, "user_gst_attempts");
            const q = query(gstAttemptsRef, where("student_id", "==", student.id));
            const gstAttemptsSnap = await getDocs(q);
            
            let attempts = [];
            gstAttemptsSnap.forEach((doc) => attempts.push(doc.data()));
            
            // Sort by timestamp ascending
            attempts.sort((a, b) => {
                const tA = a.timestamp?.toMillis ? a.timestamp.toMillis() : new Date(a.timestamp).getTime();
                const tB = b.timestamp?.toMillis ? b.timestamp.toMillis() : new Date(b.timestamp).getTime();
                return tA - tB;
            });
            
            if (attempts.length > 0) {
                setStudentGstHistory(attempts);
                setStudentGstData(attempts[attempts.length - 1]); // Show latest attempt initially
                setActiveGstAttemptIndex(attempts.length - 1);
            }

            const indAttemptsRef = collection(db, "user_ind_attempts");
            const qInd = query(indAttemptsRef, where("student_id", "==", student.id));
            const indAttemptsSnap = await getDocs(qInd);
            
            let indAttempts = [];
            indAttemptsSnap.forEach((doc) => indAttempts.push(doc.data()));
            
            indAttempts.sort((a, b) => {
                const tA = a.timestamp?.toMillis ? a.timestamp.toMillis() : new Date(a.timestamp).getTime();
                const tB = b.timestamp?.toMillis ? b.timestamp.toMillis() : new Date(b.timestamp).getTime();
                return tA - tB;
            });
            
            if (indAttempts.length > 0) {
                setStudentIndHistory(indAttempts);
                setStudentIndData(indAttempts[indAttempts.length - 1]);
                setActiveIndAttemptIndex(indAttempts.length - 1);
            } else {
                // Fallback to legacy structure if no history found
                const indDocRef = doc(db, "user_individual_assessment", student.id);
                const indDocSnap = await getDoc(indDocRef);
                if (indDocSnap.exists()) {
                    setStudentIndData(indDocSnap.data());
                }
            }

            const modDocRef = doc(db, "user_learning_modules", student.id);
            const modDocSnap = await getDoc(modDocRef);
            if (modDocSnap.exists()) {
                setStudentModuleData(modDocSnap.data());
                setTeacherNotes(modDocSnap.data().teacher_recommendation || "");
            }
        } catch (error) {
            console.error("Error fetching assessment details:", error);
        } finally {
            setLoadingAssessment(false);
        }
    };

    const handleSaveNotes = async () => {
        if (!selectedStudent) return;
        setIsSavingNotes(true);
        try {
            const modDocRef = doc(db, "user_learning_modules", selectedStudent.id);
            await setDoc(modDocRef, { teacher_recommendation: teacherNotes }, { merge: true });

            setStudentModuleData(prev => ({ ...prev, teacher_recommendation: teacherNotes }));
            setFeedbackModal({ show: true, message: "Teacher notes saved successfully!", isError: false });
        } catch (err) {
            console.error("Error saving notes", err);
            setFeedbackModal({ show: true, message: "Failed to save notes.", isError: true });
        } finally {
            setIsSavingNotes(false);
        }
    };

    const confirmAllowRetake = async () => {
        if (!selectedStudent) return;
        
        try {
            const userRef = doc(db, "users", selectedStudent.id);
            await updateDoc(userRef, {
                allow_gst_retake: true
            });
            
            // Instantly disable the button without needing a full refresh
            setSelectedStudent(prev => ({ ...prev, allow_gst_retake: true }));
            
            setShowRetakeModal(false);
            setFeedbackModal({ show: true, message: "GST Retake enabled for student!", isError: false });
        } catch (error) {
            console.error("Error allowing retake:", error);
            setShowRetakeModal(false);
            setFeedbackModal({ show: true, message: "Failed to enable retake.", isError: true });
        }
    };

    const confirmAllowIndRetake = async () => {
        if (!selectedStudent) return;
        try {
            const userRef = doc(db, "users", selectedStudent.id);
            await updateDoc(userRef, { allow_ind_retake: true });
            setSelectedStudent(prev => ({ ...prev, allow_ind_retake: true }));
            setStudents(students.map(s => s.id === selectedStudent.id ? { ...s, allow_ind_retake: true } : s));
            setShowIndRetakeModal(false);
            setFeedbackModal({ show: true, message: "Phase 2 Retake enabled for student!", isError: false });
        } catch (error) {
            console.error("Error allowing IND retake:", error);
            setShowIndRetakeModal(false);
            setFeedbackModal({ show: true, message: "Failed to enable Phase 2 retake.", isError: true });
        }
    };

    const formatDateTime = (dateVal) => {
        if (!dateVal) return 'N/A';
        const dateObj = dateVal.toDate ? dateVal.toDate() : new Date(dateVal);

        const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const month = months[dateObj.getMonth()];
        const day = dateObj.getDate();
        const year = dateObj.getFullYear();

        let hours = dateObj.getHours();
        const minutes = dateObj.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12;
        hours = hours ? hours : 12;

        return `${month} ${day}, ${year} ${hours}:${minutes}${ampm}`;
    };

    return (
        <TeacherLayout>
            <div className="bg-secondary text-white p-6 rounded-xl shadow-md flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold">Student Management</h1>
                    {/* <p className="mt-2 text-white/80 text-sm font-light">View and manage student accounts and GST results</p> */}
                    <p className="text-sm mt-1 text-blue-100">View and manage student accounts and GST results</p>
                </div>
                <img
                    src={require("../../assets/student.png")}
                    alt="student"
                    className="w-32 drop-shadow-md"
                />
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    {/* Search Input */}
                    <div className="relative flex items-center w-full md:w-72">
                        <input
                            type="text"
                            placeholder="Search students..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg pl-4 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                        {searchTerm ? (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="absolute right-3 text-gray-400 hover:text-gray-600 font-bold text-sm"
                                title="Clear search"
                            >
                                ✕
                            </button>
                        ) : (
                            <button className="absolute right-2.5 text-gray-400 p-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                                </svg>
                            </button>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        {/* Status Filter */}
                        <div className="flex items-center gap-3">
                            <label className="font-semibold text-gray-700 text-sm">Status:</label>
                            <select
                                className="border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                            >
                                <option value="ALL">All</option>
                                <option value="PENDING_GST">Pending</option>
                                <option value="PASSED_GST">Passed (GST)</option>
                                <option value="NEEDS_IND">To Assess</option>
                                <option value="COMPLETED_IND">Completed</option>
                            </select>
                        </div>

                        {/* Class Filter */}
                        <div className="flex items-center gap-3">
                            <label className="font-semibold text-gray-700 text-sm">Class:</label>
                            <select
                                className="border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                                value={selectedClassCode}
                                onChange={(e) => setSelectedClassCode(e.target.value)}
                            >
                                <option value="ALL">All Classes</option>
                                {classes.map(cls => (
                                    <option key={cls.class_code} value={cls.class_code}>
                                        {cls.subject_name} ({cls.class_code})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="w-8 h-8 border-4 border-slate-200 border-t-[#0580b2] rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse whitespace-nowrap">
                            <thead>
                                <tr className="bg-blue-100 text-blue-800">
                                    <th className="p-3 border-b cursor-pointer hover:bg-blue-200 transition select-none group" onClick={() => handleStudentSort("date_registered")}>
                                        <div className="flex items-center gap-1">Date Registered <span className={`text-blue-500 group-hover:text-blue-700 ${studentSortConfig.key !== "date_registered" ? "opacity-30" : ""}`}>{studentSortConfig.key === "date_registered" && studentSortConfig.direction === "desc" ? "↓" : "↑"}</span></div>
                                    </th>
                                    <th className="p-3 border-b cursor-pointer hover:bg-blue-200 transition select-none group" onClick={() => handleStudentSort("name")}>
                                        <div className="flex items-center gap-1">Name <span className={`text-blue-500 group-hover:text-blue-700 ${studentSortConfig.key !== "name" ? "opacity-30" : ""}`}>{studentSortConfig.key === "name" && studentSortConfig.direction === "desc" ? "↓" : "↑"}</span></div>
                                    </th>
                                    <th className="p-3 border-b cursor-pointer hover:bg-blue-200 transition select-none group" onClick={() => handleStudentSort("grade")}>
                                        <div className="flex items-center gap-1">Grade Level <span className={`text-blue-500 group-hover:text-blue-700 ${studentSortConfig.key !== "grade" ? "opacity-30" : ""}`}>{studentSortConfig.key === "grade" && studentSortConfig.direction === "desc" ? "↓" : "↑"}</span></div>
                                    </th>

                                    <th className="p-3 border-b text-center cursor-pointer hover:bg-blue-200 transition select-none group" onClick={() => handleStudentSort("status")}>
                                        <div className="flex items-center justify-center gap-1">Status <span className={`text-blue-500 group-hover:text-blue-700 ${studentSortConfig.key !== "status" ? "opacity-30" : ""}`}>{studentSortConfig.key === "status" && studentSortConfig.direction === "desc" ? "↓" : "↑"}</span></div>
                                    </th>
                                    <th className="p-3 border-b text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedStudents.length > 0 ? (
                                    paginatedStudents.map(student => (
                                        <tr key={student.id} className="hover:bg-gray-50 border-b">
                                            <td className="p-3 text-sm text-gray-600 whitespace-nowrap font-medium">
                                                {formatDateTime(student.createdAt)}
                                            </td>
                                            <td className="p-3 font-medium">{student.first_name} {student.last_name}</td>
                                            <td className="p-3">{student.grade_level}</td>

                                            <td className="p-3 text-center">
                                                {getStatusBadge(getStudentStatus(student))}
                                            </td>
                                            <td className="p-3 text-center">
                                                <button
                                                    onClick={() => handleViewAssessment(student)}
                                                    className="px-3 py-1 bg-blue-50 text-blue-600 border border-blue-200 rounded hover:bg-blue-100 transition text-sm">
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="p-6 text-center text-gray-500">
                                            No students found for this selection.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination Controls */}
                {!loading && displayedStudents.length > 0 && (
                    <div className="flex flex-col sm:flex-row justify-between items-center mt-4 pt-4 border-t border-gray-200 gap-4 text-sm text-gray-600">
                        <div>
                            Showing <span className="font-semibold text-gray-800">{startIndex + 1}</span> to{" "}
                            <span className="font-semibold text-gray-800">
                                {Math.min(startIndex + rowsPerPage, totalStudents)}
                            </span>{" "}
                            of <span className="font-semibold text-gray-800">{totalStudents}</span> entries
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                                <label className="text-gray-600 text-xs font-semibold">Rows per page:</label>
                                <select
                                    value={rowsPerPage}
                                    onChange={(e) => {
                                        setRowsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                </select>
                            </div>

                            <div className="flex items-center space-x-1">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent font-medium transition"
                                >
                                    Previous
                                </button>

                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        className={`px-3 py-1 rounded font-medium transition ${currentPage === page
                                            ? "bg-blue-600 text-white border border-blue-600 shadow-sm"
                                            : "border border-gray-300 hover:bg-gray-100 text-gray-700 bg-white"
                                            }`}
                                    >
                                        {page}
                                    </button>
                                ))}

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent font-medium transition"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Assessment Details Modal */}
            {assessmentModalOpen && selectedStudent && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
                    <div className="min-h-screen px-4 justify-center items-center flex py-10">
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl">
                            <div className="p-6 border-b flex justify-between items-center bg-blue-50 rounded-t-xl sticky top-0 z-10">
                                <h2 className="text-2xl font-bold text-blue-800">Assessment Details - {selectedStudentName}</h2>
                                <button
                                    onClick={() => setAssessmentModalOpen(false)}
                                    className="text-gray-500 hover:text-gray-800 text-2xl"
                                >
                                    &times;
                                </button>
                            </div>

                            <div className="flex border-b bg-gray-100 sticky top-[80px] z-10">
                                <button
                                    className={`flex-1 py-3 font-semibold text-center transition ${activeTab === 'overview' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-200'}`}
                                    onClick={() => setActiveTab('overview')}
                                >
                                    Overview
                                </button>
                                <button
                                    className={`flex-1 py-3 font-semibold text-center transition ${activeTab === 'gst' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-200'}`}
                                    onClick={() => setActiveTab('gst')}
                                >
                                    GST
                                </button>
                                <button
                                    className={`flex-1 py-3 font-semibold text-center transition ${activeTab === 'ind' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-200'}`}
                                    onClick={() => setActiveTab('ind')}
                                >
                                    Individualized Assessment
                                </button>
                                <button
                                    className={`flex-1 py-3 font-semibold text-center transition ${activeTab === 'orp' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-200'}`}
                                    onClick={() => setActiveTab('orp')}
                                >
                                    Oral Reading Profile
                                </button>
                                <button
                                    className={`flex-1 py-3 font-semibold text-center transition ${activeTab === 'modules' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-200'}`}
                                    onClick={() => setActiveTab('modules')}
                                >
                                    Learning Modules
                                </button>
                            </div>

                            <div className="p-6 bg-gray-50 rounded-b-xl">
                                {loadingAssessment ? (
                                    <p className="text-center text-gray-500 py-10">Loading answers...</p>
                                ) : (
                                    <div>
                                        {/* Overview Section */}
                                        {activeTab === 'overview' && (
                                            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 animate-fadeIn">
                                                <h3 className="text-xl font-bold text-gray-800 mb-6 border-b pb-2">Student Overview</h3>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 shadow-sm">
                                                        <p className="text-sm text-gray-500 font-semibold mb-1">Class Code</p>
                                                        <p className="text-lg font-mono font-bold text-blue-800">{selectedStudent?.classCode || 'N/A'}</p>
                                                    </div>
                                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 shadow-sm">
                                                        <p className="text-sm text-gray-500 font-semibold mb-1">GST Date</p>
                                                        <p className="text-lg font-bold text-gray-800">
                                                            {selectedStudent?.assessment_date ? new Date(selectedStudent.assessment_date).toLocaleDateString() : 'N/A'}
                                                        </p>
                                                    </div>
                                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 shadow-sm">
                                                        <p className="text-sm text-gray-500 font-semibold mb-1">GST Score</p>
                                                        <p className="text-lg font-bold text-gray-800">
                                                            {selectedStudent?.gst_assessment_attempted ? `${selectedStudent.gst_score} / ${selectedStudent.gst_total_questions || '-'}` : 'Not Attempted'}
                                                        </p>
                                                    </div>
                                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 shadow-sm">
                                                        <p className="text-sm text-gray-500 font-semibold mb-1">Overall Status</p>
                                                        <div className="mt-1">
                                                            {selectedStudent ? getStatusBadge(getStudentStatus(selectedStudent)) : 'N/A'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* GST Section */}
                                        {activeTab === 'gst' && (
                                            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 animate-fadeIn">
                                                <div className="flex justify-between items-center mb-4 border-b pb-2">
                                                    <h3 className="text-xl font-bold text-gray-800">Group Screening Test Data</h3>
                                                    <button 
                                                        onClick={() => setShowRetakeModal(true)}
                                                        disabled={selectedStudent?.allow_gst_retake === true || studentGstHistory.length >= 3}
                                                        className={`px-4 py-1.5 rounded-md text-sm font-semibold transition shadow-sm ${
                                                            selectedStudent?.allow_gst_retake === true || studentGstHistory.length >= 3
                                                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                                                : "bg-yellow-500 hover:bg-yellow-600 text-white"
                                                        }`}
                                                    >
                                                        {selectedStudent?.allow_gst_retake === true 
                                                            ? "Retake Pending" 
                                                            : studentGstHistory.length >= 3 
                                                                ? "Max Tries Reached" 
                                                                : "Allow GST Retake"}
                                                    </button>
                                                </div>

                                                {/* History Tabs */}
                                                {studentGstHistory.length > 1 && (
                                                    <div className="flex gap-2 mb-4 bg-gray-100 p-1 rounded-md w-fit">
                                                        {studentGstHistory.map((_, idx) => (
                                                            <button
                                                                key={idx}
                                                                onClick={() => {
                                                                    setActiveGstAttemptIndex(idx);
                                                                    setStudentGstData(studentGstHistory[idx]);
                                                                }}
                                                                className={`px-4 py-1.5 text-sm font-semibold rounded-md transition ${activeGstAttemptIndex === idx ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                                                            >
                                                                Try {idx + 1}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}

                                                {studentGstData ? (
                                                    <div>
                                                        <div className="flex justify-between items-center mb-4">
                                                            <p className="font-semibold text-lg text-blue-700">
                                                                Score: {studentGstData.score} / {studentGstData.total_questions}
                                                            </p>
                                                            <p className="text-sm text-gray-500 font-medium">
                                                                Attempted on: {studentGstData.timestamp ? formatDateTime(studentGstData.timestamp) : 'N/A'}
                                                            </p>
                                                        </div>
                                                        <div className="space-y-4">
                                                            {studentGstData.answers?.map((ans, idx) => (
                                                                <div key={idx} className={`p-4 rounded-md border-l-4 ${ans.is_correct ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
                                                                    <p className="font-medium text-gray-800"><span className="text-sm text-gray-500">Q{idx + 1}.</span> {ans.question_text}</p>
                                                                    <div className="mt-2 text-sm grid grid-cols-1 md:grid-cols-2 gap-2">
                                                                        <p><span className="font-semibold text-gray-600">Student's Answer:</span> {ans.selected_text || <span className="italic text-gray-400">No answer</span>}</p>
                                                                        {!ans.is_correct && (
                                                                            <p className="text-green-700 font-semibold"><span className="text-gray-600">Correct Answer:</span> {ans.correct_text}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500 italic">No GST data found for this student.</p>
                                                )}
                                            </div>
                                        )}

                                        {/* Individualized Assessment Section */}
                                        {activeTab === 'ind' && (
                                            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 animate-fadeIn">
                                                <div className="flex justify-between items-center mb-4 border-b pb-2">
                                                    <h3 className="text-xl font-bold text-gray-800">Comprehension Questions</h3>
                                                    <button 
                                                        onClick={() => setShowIndRetakeModal(true)}
                                                        disabled={selectedStudent?.allow_ind_retake === true || studentIndHistory.length >= 3}
                                                        className={`px-4 py-1.5 rounded-md text-sm font-semibold transition shadow-sm ${
                                                            selectedStudent?.allow_ind_retake === true || studentIndHistory.length >= 3
                                                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                                                : "bg-indigo-500 hover:bg-indigo-600 text-white"
                                                        }`}
                                                    >
                                                        {selectedStudent?.allow_ind_retake === true 
                                                            ? "Retake Pending" 
                                                            : studentIndHistory.length >= 3 
                                                                ? "Max Tries Reached" 
                                                                : "Allow Phase 2 Retake"}
                                                    </button>
                                                </div>

                                                {/* History Tabs */}
                                                {studentIndHistory.length > 0 && (
                                                    <div className="flex gap-2 mb-4 bg-gray-100 p-1 rounded-md w-fit">
                                                        {studentIndHistory.map((_, idx) => (
                                                            <button
                                                                key={idx}
                                                                onClick={() => {
                                                                    setActiveIndAttemptIndex(idx);
                                                                    setStudentIndData(studentIndHistory[idx]);
                                                                }}
                                                                className={`px-4 py-1.5 text-sm font-semibold rounded-md transition ${activeIndAttemptIndex === idx ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                                                            >
                                                                Try {idx + 1}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                                {studentIndData ? (
                                                    <div>
                                                        <div className="flex justify-end items-center mt-2">
                                                            <p className="text-sm text-gray-500 font-medium">
                                                                Attempted on: {studentIndData.timestamp ? formatDateTime(studentIndData.timestamp) : 'N/A'}
                                                            </p>
                                                        </div>
                                                        <div className="space-y-4 mt-2">
                                                            {studentIndData.answers?.map((ans, idx) => (
                                                                <div key={idx} className={`p-4 rounded-md border-l-4 ${ans.is_correct ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
                                                                    <p className="font-medium text-gray-800"><span className="text-sm text-gray-500">Q{idx + 1}.</span> {ans.question_text}</p>
                                                                    <p className="text-xs text-gray-500 mt-1 mb-2">Story: {ans.story_title}</p>
                                                                    <div className="mt-2 text-sm grid grid-cols-1 md:grid-cols-2 gap-2">
                                                                        <p><span className="font-semibold text-gray-600">Student's Answer:</span> {ans.selected_text || <span className="italic text-gray-400">No answer</span>}</p>
                                                                        {!ans.is_correct && (
                                                                            <p className="text-green-700 font-semibold"><span className="text-gray-600">Correct Answer:</span> {ans.correct_text}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                            {(!studentIndData.answers || studentIndData.answers.length === 0) && (
                                                                <p className="text-gray-500 italic">No comprehension answers recorded.</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500 italic">No individualized assessment data found.</p>
                                                )}
                                            </div>
                                        )}

                                        {/* Oral Reading Profile Section */}
                                        {activeTab === 'orp' && (
                                            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 animate-fadeIn">
                                                <h3 className="text-xl font-bold mb-4 text-gray-800 border-b pb-2">Overall Profile Metrics</h3>
                                                {selectedStudent.gst_score >= 14 || selectedStudent.gst_status === 'PASSED_GST' ? (
                                                    <div className="space-y-6 text-lg">
                                                        <div className="bg-green-50 p-6 rounded-lg border border-green-200 text-center">
                                                            <h4 className="text-xl font-bold text-green-800 mb-2">Exempt from Remediation (Passed GST)</h4>
                                                            <p className="text-green-700 text-base">
                                                                This student scored <strong>{selectedStudent.gst_score} / {selectedStudent.gst_total_questions || '-'}</strong> on the Group Screening Test (GST), exceeding the threshold of 14. They will receive regular classroom instruction without individualized reading remediation.
                                                            </p>
                                                        </div>
                                                        <div className="pt-4 border-t flex items-center justify-between">
                                                            <h3 className="text-xl font-bold text-gray-800">GST Assessment Status:</h3>
                                                            <span className="text-2xl font-black text-green-600">
                                                                Passed (No Remediation Needed)
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : (selectedStudent.individualized_assessment_attempted || studentIndData) ? (
                                                    <div className="space-y-6 text-lg">
                                                        {/* History Tabs */}
                                                        {studentIndHistory.length > 0 && (
                                                            <div className="flex gap-2 mb-4 bg-gray-100 p-1 rounded-md w-fit">
                                                                {studentIndHistory.map((_, idx) => (
                                                                    <button
                                                                        key={idx}
                                                                        onClick={() => {
                                                                            setActiveIndAttemptIndex(idx);
                                                                            setStudentIndData(studentIndHistory[idx]);
                                                                        }}
                                                                        className={`px-4 py-1.5 text-sm font-semibold rounded-md transition ${activeIndAttemptIndex === idx ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                                                                    >
                                                                        Try {idx + 1}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}

                                                        <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg border">
                                                            <span className="font-semibold text-gray-700">Word Reading Level:</span>
                                                            <div className="text-right">
                                                                <span className="font-medium mr-3">{studentIndData?.individualized_score ?? selectedStudent.individualized_score}%</span>
                                                                <span className="text-blue-700 font-bold">{studentIndData?.word_reading_level || selectedStudent.word_reading_level || "N/A"}</span>
                                                            </div>
                                                        </div>

                                                        <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg border">
                                                            <span className="font-semibold text-gray-700">Comprehension Level:</span>
                                                            <div className="text-right">
                                                                <span className="font-medium mr-3">{studentIndData?.individualized_comprehension_percentage ?? selectedStudent.individualized_comprehension_percentage ?? 0}%</span>
                                                                <span className="text-blue-700 font-bold">{studentIndData?.comprehension_level || selectedStudent.comprehension_level || "N/A"}</span>
                                                            </div>
                                                        </div>

                                                        <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg border">
                                                            <span className="font-semibold text-gray-700">Reading Rate:</span>
                                                            <span className="font-medium text-gray-800">{studentIndData?.word_per_minute ?? selectedStudent.word_per_minute ?? 0} words per minute</span>
                                                        </div>

                                                        <div className="mt-8 pt-4 border-t">
                                                            <div className="flex items-center justify-between">
                                                                <h3 className="text-xl font-bold text-gray-800">Oral Reading Profile:</h3>
                                                                <span className={`text-2xl font-black ${(studentIndData?.oral_reading_profile || selectedStudent.oral_reading_profile) === 'Independent' ? 'text-green-600' :
                                                                    (studentIndData?.oral_reading_profile || selectedStudent.oral_reading_profile) === 'Instructional' ? 'text-blue-600' : 'text-red-500'
                                                                    }`}>
                                                                    {studentIndData?.oral_reading_profile || selectedStudent.oral_reading_profile || "Pending"}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {studentIndData?.audio_recording_url && (
                                                            <div className="mt-6 bg-gray-50 border border-gray-200 p-6 rounded-lg text-center">
                                                                <h3 className="font-bold text-gray-700 mb-3 text-sm uppercase tracking-wider">Student's Reading Recording</h3>
                                                                <audio controls src={studentIndData.audio_recording_url} className="w-full max-w-md mx-auto" />
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500 italic">This student has not yet completed the individualized assessment.</p>
                                                )}
                                            </div>
                                        )}

                                        {/* Learning Modules Section */}
                                        {activeTab === 'modules' && (
                                            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 animate-fadeIn">
                                                <h3 className="text-xl font-bold mb-4 text-gray-800 border-b pb-2">Personalized Learning Modules</h3>

                                                {(studentModuleData?.overview || studentModuleData?.ai_recommendation) ? (
                                                    <div className="mb-6 space-y-6">
                                                        {/* Overview Card */}
                                                        <div className="bg-blue-50 border border-blue-100 p-6 rounded-xl shadow-sm">
                                                            <h4 className="font-bold text-blue-800 mb-2 uppercase text-sm tracking-wide">Performance Overview</h4>
                                                            <div className="prose text-gray-800 whitespace-pre-line">
                                                                {studentModuleData.overview || studentModuleData.ai_recommendation}
                                                            </div>
                                                        </div>

                                                        {/* Targeted Strategies */}
                                                        {studentModuleData.targeted_strategies && studentModuleData.targeted_strategies.length > 0 && (
                                                            <div>
                                                                <h4 className="font-bold text-gray-800 mb-3 uppercase text-sm tracking-wide">🎯 Targeted Strategies</h4>
                                                                <div className="grid md:grid-cols-3 gap-4">
                                                                    {studentModuleData.targeted_strategies.map((strat, i) => (
                                                                        <div key={i} className="bg-white border text-sm p-4 rounded-lg shadow-sm">
                                                                            <h5 className="font-bold text-blue-900 mb-1">{strat.title}</h5>
                                                                            <p className="text-gray-600">{strat.description}</p>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Recommended Materials */}
                                                        {studentModuleData.recommended_materials && studentModuleData.recommended_materials.length > 0 && (
                                                            <div>
                                                                <h4 className="font-bold text-gray-800 mb-3 uppercase text-sm tracking-wide">📚 Recommended Materials</h4>
                                                                <div className="grid md:grid-cols-3 gap-4">
                                                                    {studentModuleData.recommended_materials.map((mat, i) => (
                                                                        <div key={i} className="bg-white border text-sm p-4 rounded-lg shadow-sm">
                                                                            <h5 className="font-bold text-green-900 mb-1">{mat.title}</h5>
                                                                            <p className="text-gray-600">{mat.description}</p>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        <div className="pt-4 border-t border-gray-100">
                                                            <h4 className="font-bold text-gray-800 mb-2 uppercase text-sm tracking-wide">Teacher's Additional Recommendation (Optional)</h4>
                                                            <textarea
                                                                className="w-full border border-gray-300 rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-blue-400 min-h-[150px]"
                                                                placeholder="Add your manual recommendations, assignments, or notes for the student..."
                                                                value={teacherNotes}
                                                                onChange={(e) => setTeacherNotes(e.target.value)}
                                                            />
                                                            <button
                                                                onClick={handleSaveNotes}
                                                                disabled={isSavingNotes}
                                                                className="mt-3 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded shadow transition disabled:opacity-50"
                                                            >
                                                                {isSavingNotes ? "Saving..." : "Save Teacher Notes"}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-8">
                                                        <p className="text-gray-500 italic mb-4">No AI recommendation has been generated yet.</p>
                                                        <p className="text-sm text-gray-400">The student needs to log into their 'Lessons' page to trigger the AI generation.</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Retake Confirmation Modal */}
            {showRetakeModal && selectedStudent && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 60 }}>
                    <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full animate-fadeIn">
                        <div className="w-16 h-16 bg-yellow-100 text-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">Allow GST Retake?</h2>
                        <p className="text-gray-600 mb-8 text-center">
                            Are you sure you want to allow this student to retake the GST? 
                            Their current score will be preserved in their history.
                        </p>
                        <div className="flex gap-4">
                            <button
                                onClick={() => setShowRetakeModal(false)}
                                className="flex-1 py-2.5 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmAllowRetake}
                                className="flex-1 py-2.5 bg-yellow-500 text-white font-bold rounded-lg hover:bg-yellow-600 transition"
                            >
                                Confirm Retake
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom IND Retake Confirmation Modal */}
            {showIndRetakeModal && selectedStudent && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 60 }}>
                    <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full animate-fadeIn">
                        <div className="flex justify-center mb-6">
                            <div className="bg-indigo-100 p-4 rounded-full text-indigo-600">
                                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>
                            </div>
                        </div>
                        
                        <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">
                            Allow Phase 2 Retake?
                        </h2>
                        
                        <p className="text-center text-gray-600 mb-8">
                            Are you sure you want to allow <span className="font-bold text-gray-800">{selectedStudentName}</span> to retake the <span className="font-semibold">Individualized Assessment</span>?
                            Their previous attempts will be securely stored in history.
                        </p>

                        <div className="flex gap-4 w-full">
                            <button
                                onClick={() => setShowIndRetakeModal(false)}
                                className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold rounded-lg transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmAllowIndRetake}
                                className="flex-1 py-3 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg shadow-md transition"
                            >
                                Allow Retake
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Feedback Modal */}
            {feedbackModal.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 60 }}>
                    <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-sm w-full text-center transform animate-fadeIn">
                        {feedbackModal.isError ? (
                            <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-3xl font-bold">!</span>
                            </div>
                        ) : (
                            <div className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                            </div>
                        )}
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">
                            {feedbackModal.isError ? "Error" : "Success"}
                        </h2>
                        <p className="text-gray-600 mb-8">{feedbackModal.message}</p>
                        <button
                            onClick={() => setFeedbackModal({ show: false, message: "", isError: false })}
                            className={`w-full font-bold py-3 px-8 rounded-full shadow transition ${feedbackModal.isError ? "bg-red-600 hover:bg-red-700 text-white" : "bg-green-600 hover:bg-green-700 text-white"
                                }`}
                        >
                            Okay
                        </button>
                    </div>
                </div>
            )}
        </TeacherLayout>
    );
}
